import { useContext } from "react";

import { StateContext } from "../context/globalContext/context";
import { iGlobalContext } from "../context/globalContext/interface";
import { checkHotelRoomAvailable } from "../api/hotelDetailsAPI";
import { fetchFairDetails, getFlightAvailability } from "../api/booking/booking";
import { iSharedCartPayload } from "../api/sharedCart/sharedCart";
import { resolveHotelSessionId } from "../utils/booking/hotel/resolveHotelSessionId";
import { iMappedSharedCart } from "../utils/sharedCart/mapSharedCartDetails";
import { consoleLog } from "../utils/consoleLog/consoleLog";

const availabilityFields = (response: any, room: any) => {
  const entry = response?.data?.[0];
  return {
    preBookId: entry?.prebookId ?? "NA",
    sessionId: entry?.sessionId ?? room?.sessionId ?? "NA",
    responseUniqueId: entry?.uniqueId ?? "NA",
    priceChanged: entry?.pricing?.priceChanged ?? entry?.priceChanged ?? response?.priceChanged ?? false,
    updatedPrice: entry?.pricing?.sellingPrice ?? null,
    previousPrice: entry?.pricing?.previousPrice ?? null,
    hotelAvailabilityResponseId: response?.hotelAvailabilityResponseId ?? null,
    availabilityChecked: true,
  };
};

export function useSharedCartAvailability() {
  const {
    accessJWT,
    userInfo,
    setSelectedHotelRoomArr,
    setFlightAvailabilityData,
    setBookingDetails,
    setFareDetails,
    setSelectedFareContext,
    setFareId,
  }: iGlobalContext = useContext(StateContext);

  async function checkStay(stay: any) {
    const room = stay?.selectedHotelRoom;
    if (!room) return;
    const body = {
      userId: userInfo?.id,
      hotels: [
        {
          uniqueId: room.selectedRoomId,
          sessionId: resolveHotelSessionId(room.roomPricingSessionId),
          supplier: room.supplier,
          hotelId: room.rateHotelId,
          unicaId: room.unicaId,
        },
      ],
    };
    const response = await checkHotelRoomAvailable(accessJWT, body, new AbortController(), () => {}).catch(() => undefined);
    const fields = availabilityFields(response, room);
    consoleLog(["SHARED CART HOTEL AVAILABILITY", { room: room.selectedRoomId, preBookId: fields.preBookId, priceChanged: fields.priceChanged }]);
    setSelectedHotelRoomArr((prev: any) =>
      prev.map((item: any) =>
        item?.selectedHotelRoom?.selectedRoomId === room.selectedRoomId &&
        item?.checkIn === stay.checkIn &&
        item?.checkOut === stay.checkOut
          ? { ...item, selectedHotelRoom: { ...item.selectedHotelRoom, ...fields } }
          : item
      )
    );
  }

  async function checkFlight(flight: any, fareId: string) {
    let data: any = null;
    let available = false;
    try {
      const response: any = await getFlightAvailability(
        accessJWT,
        flight.encryptedKey,
        flight.uniqueId,
        new AbortController(),
        fareId,
        userInfo?.id ?? ""
      );
      data = response?.data ?? response?.rawError?.data ?? null;
      available = response?.statusCode === 200 && !!data?.transactionId;
    } catch {}
    consoleLog(["SHARED CART FLIGHT AVAILABILITY", { flight: flight.uniqueId, available }]);
    setFlightAvailabilityData((prev: any) => {
      if (prev?.uniqueId !== flight.uniqueId) return prev;
      return available
        ? {
            ...prev,
            ...data,
            fareId,
            encryptedKey: prev.encryptedKey,
            uniqueId: prev.uniqueId,
            flightPrice: prev.flightPrice,
            isDocsMandatory: prev?.isDocsMandatory ?? false,
            extraServiceDetails: prev?.extraServiceDetails ?? {},
            availabilityChecked: true,
          }
        : { ...prev, availabilityChecked: true };
    });
    setBookingDetails((prev: any) =>
      prev?.selectedFlightId === flight.uniqueId ? { ...prev, isFlightAvailableForPrebooking: available } : prev
    );
  }

  async function loadFares(flight: any, fareId: string) {
    setFareId(fareId);
    const response: any = await fetchFairDetails(
      accessJWT,
      flight.encryptedKey,
      flight.uniqueId,
      new AbortController(),
      userInfo?.id ?? "",
      "v3"
    ).catch(() => undefined);
    const fares: any[] = response?.statusCode === 200 && Array.isArray(response?.data) ? response.data : [];
    const found = fares.some((fare) => fare?.fare?.id === fareId);
    consoleLog(["SHARED CART FLIGHT FARES", { flight: flight.uniqueId, fares: fares.length, found }]);
    if (!fares.length) return;
    setFareDetails(fares);
    setSelectedFareContext({
      encryptedKey: flight.encryptedKey,
      uniqueId: flight.uniqueId,
      fareIds: fares.map((fare) => fare?.fare?.id ?? "").filter(Boolean),
    });
  }

  return (mapped: iMappedSharedCart, snapshot: iSharedCartPayload) => {
    mapped.selectedHotelRoomArr.forEach((stay) => void checkStay(stay));
    const flight = mapped.flightAvailabilityData;
    if (!mapped.selectedFlightOptions.flights || !flight) return;
    const fareId = snapshot?.flight?.fareId || flight.fareId;
    void checkFlight(flight, fareId);
    void loadFares(flight, fareId);
  };
}
